import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Building2, 
  FileText, 
  CreditCard, 
  Repeat, 
  Receipt, 
  Upload,
  Wallet,
  Menu,
  X,
  LogOut,
  TrendingUp,
  BarChart3,
  Users,
  Bell,
  FolderKanban,
  Plus
} from "lucide-react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/bank-accounts", icon: Building2, label: "Bank Accounts" },
  { path: "/invoices", icon: FileText, label: "Invoices" },
  { path: "/estimated-inflows", icon: TrendingUp, label: "Estimated Inflows" },
  { path: "/payables", icon: Wallet, label: "Payables" },
  { path: "/credit-cards", icon: CreditCard, label: "Credit Cards" },
  { path: "/fixed-expenses", icon: Repeat, label: "Fixed Expenses" },
  { path: "/variable-expenses", icon: Receipt, label: "Variable Expenses" },
  { path: "/projects", icon: FolderKanban, label: "Projects" },
  { path: "/analytics", icon: BarChart3, label: "Trend Analysis" },
  { path: "/upload", icon: Upload, label: "Upload Data" },
  { path: "/alerts", icon: Bell, label: "Email Alerts" },
  { path: "/users", icon: Users, label: "User Management", adminOnly: true },
];

const Sidebar = ({ onNavClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  return (
    <div className="h-full flex flex-col bg-brand-navy text-white">
      {/* Logo */}
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/logos/empact-logo.jpg" alt="Empact Studio" className="w-10 h-10 rounded-lg object-contain shadow-md bg-white" />
          <div>
            <h1 className="font-bold text-lg tracking-tight text-white">Empact Resource</h1>
            <p className="text-xs text-white/60">Financial Dashboard</p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems
          .filter((item) => !item.adminOnly || user?.role === "admin")
          .map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
              className={`sidebar-nav-item ${isActive ? "active" : ""}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      
      {/* User Info & Logout */}
      <div className="p-4 border-t border-white/10">
        {user && (
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-white/90 truncate">{user.name || user.email}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-white/50 truncate">{user.email}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                user.role === 'admin' ? 'bg-brand-orange/20 text-brand-orange' :
                user.role === 'full_access' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-slate-500/20 text-slate-400'
              }`}>
                {user.role === 'admin' ? 'Admin' : user.role === 'full_access' ? 'Full' : 'View'}
              </span>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const quickActions = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/fixed-expenses", icon: Repeat, label: "Fixed Exp" },
    { path: "/payables", icon: Wallet, label: "Payables" },
    { path: "/invoices", icon: FileText, label: "Invoices" },
    { path: "/projects", icon: FolderKanban, label: "Projects" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-brand-navy h-14 flex items-center justify-between px-4 shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/logos/empact-logo.jpg" alt="Empact Studio" className="w-9 h-9 rounded-lg object-contain bg-white" />
          <span className="font-bold text-white">Empact Resource</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar - Desktop: Static, Mobile: Slide-in */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 shadow-xl transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:w-60
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:block
        `}
      >
        <Sidebar onNavClick={closeMobileMenu} />
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-60 pt-14 pb-16 lg:pt-0 lg:pb-0 overflow-x-hidden">
        <div className="min-h-screen">
          <div className="max-w-[1600px] mx-auto px-3 py-4 sm:px-4 lg:px-6 lg:py-6">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Mobile Quick Actions Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-brand-navy border-t border-white/10 shadow-[0_-2px_10px_rgba(0,0,0,0.15)]" data-testid="quick-actions-bar">
        <div className="flex items-center justify-around h-14 px-1">
          {quickActions.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                  isActive ? 'text-brand-orange' : 'text-white/60 active:text-white'
                }`}
                data-testid={`quick-${item.path.replace("/", "") || "dashboard"}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
